import { CopyOutlined, DownOutlined, SaveOutlined } from '@ant-design/icons'
import { DriveFile, OrganizationMember } from '@prisma/client'
import { Button, Dropdown, Flex, MenuProps, message, Typography } from 'antd'
import { marked } from 'marked'
import { useEffect, useState } from 'react'
import { MarkdownEditor } from '~/plugins/markdown/client/components/Editor'
import { useDriveFile } from '../../hooks/useDriveFile'
import { PlaceholderEmpty } from '../PlaceholderEmpty'

type Props = {
  driveFileId: string
  organizationMember: OrganizationMember
  onSave?: (driveFileUpdated: Partial<DriveFile>) => Promise<void>
}

export const TemplateEditor: React.FC<Props> = ({
  driveFileId,
  organizationMember,
  onSave,
}) => {
  const { driveFile, updateDriveFile, isLoading, refetch } = useDriveFile({
    driveFileId,
    organizationMember,
  })

  const [content, setContent] = useState<string>(driveFile?.content ?? '')
  const [name, setName] = useState<string>(driveFile?.name)

  const isDirty = content !== driveFile?.content || name !== driveFile?.name

  const handleChange = (contentUpdated: string) => {
    setContent(contentUpdated)
  }

  const handleSave = async () => {
    const driveFileUpdated = { driveFileId: driveFile.id, name, content }
    await updateDriveFile(driveFileUpdated)

    await onSave?.(driveFileUpdated)
  }

  useEffect(() => {
    if (driveFile) {
      setName(driveFile.name)
      setContent(driveFile.content ?? '')
    }
  }, [driveFile])

  const copyItems: MenuProps['items'] = [
    {
      key: 'markdown',
      label: 'Copy Markdown',
      onClick: async () => {
        try {
          await navigator.clipboard.writeText(content)
          message.success('Markdown copied')
        } catch (error) {
          message.error('Failed to copy Markdown')
        }
      },
    },
    {
      key: 'html',
      label: 'Copy HTML',
      onClick: async () => {
        try {
          const html = await marked.parse(content)
          await navigator.clipboard.writeText(html)
          message.success('HTML copied')
        } catch (error) {
          message.error('Failed to copy HTML')
        }
      },
    },
  ]

  if (!driveFile) {
    return (
      <>
        <Flex vertical className="w-full h-full">
          <PlaceholderEmpty />
        </Flex>
      </>
    )
  }

  return (
    <Flex
      vertical
      className="w-full h-full mx-auto"
      style={{ overflowY: 'auto' }}
    >
      <Flex
        justify="space-around"
        align="center"
        className="px-8 py-8 gap-4"
        wrap
      >
        <Typography.Paragraph
          editable={{ onChange: setName }}
          className=" flex-grow"
          style={{ marginBottom: 0 }}
        >
          {name}
        </Typography.Paragraph>

        <Flex gap={8}>
          <Dropdown menu={{ items: copyItems }}>
            <Button size="small" icon={<CopyOutlined />}>
              Copy <DownOutlined />
            </Button>
          </Dropdown>

          <Button
            type="primary"
            size="small"
            disabled={!isDirty}
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={isLoading}
          >
            Save
          </Button>
        </Flex>
      </Flex>

      <Flex vertical gap={16} className="px-4">
        <Flex
          className="h-full w-max-[800px] mx-auto border-gray-200 rounded-lg"
          style={{ overflowY: 'auto' }}
        >
          <MarkdownEditor
            value={driveFile?.content ?? ''}
            onChange={handleChange}
          />
        </Flex>
      </Flex>
    </Flex>
  )
}
